import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal
} from "@angular/core";

import { LktNotificationItem, LktNotificationToastComponent } from "@frontends-v3/design-system";

import { NotificationNavigationService } from "../../services/notifications/notification-navigation.service";
import { NotificationsStateService } from "../../services/notifications/notifications-state.service";

import { buildNotificationItem } from "./utils/build-notification-item.util";

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
/** Durações do protótipo (css/notificacoes.css: nt-toast-in / nt-toast-out). */
const ENTER_MS = 340;
/**
 * Espelha por literal `--motion-dur-fast` (`variables.css:251` = 220ms), a mesma duração de
 * `nt-toast-out` no SCSS do toast. Não há como consumir o token direto num `.ts`: manter os dois
 * em 220 é responsabilidade de quem editar um dos dois lados.
 */
const LEAVE_MS = 220;

/**
 * Container dos avisos temporários (DEV-872). Montado nos shells do admin e da plataforma.
 *
 * Divisão de responsabilidade: o `lkt-notification-toast` é dono do próprio timer de vida e avisa
 * quando expirou; este container é dono da FILA e do momento de remover do DOM. A remoção espera a
 * animação de saída — tirar o elemento na hora não deixaria saída nenhuma para animar.
 *
 * O teto de três avisos vivos é aplicado no estado, não aqui.
 */
@Component({
  selector: "app-notifications-toaster",
  standalone: true,
  imports: [LktNotificationToastComponent],
  templateUrl: "./notifications-toaster.container.html",
  styleUrl: "./notifications-toaster.container.scss",
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NotificationsToasterContainerComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly navigation = inject(NotificationNavigationService);
  public readonly state = inject(NotificationsStateService);

  private readonly reducedMotion = signal<boolean>(false);
  private readonly entering = signal<ReadonlySet<string>>(new Set<string>());
  private readonly leaving = signal<ReadonlySet<string>>(new Set<string>());

  /** Espelho da fila viva, não histórico da sessão: podado no effect. Ver `pruneSeen()`. */
  private readonly seen = new Set<string>();
  private readonly timers = new Set<ReturnType<typeof globalThis.setTimeout>>();

  /** `false` = movimento reduzido: sem animação e, no toast, sem auto-fechar (CA 08). */
  public readonly animate = computed<boolean>(() => !this.reducedMotion());

  public readonly viewToasts = computed<LktNotificationItem[]>(() => {
    const now = new Date();
    return this.state.toasts().map((dto) => buildNotificationItem(dto, now));
  });

  constructor() {
    this.watchReducedMotion();

    // Marca os recém-chegados para a animação de entrada e desmarca depois dela.
    effect(() => {
      const liveIds = this.state.toasts().map((toast) => toast.id);
      // Poda ANTES do early return: sem isso a poda não roda em remoção (nenhum id novo, `fresh`
      // vazio) e o Set cresceria por toda a sessão.
      this.pruneSeen(liveIds);

      const fresh = liveIds.filter((id) => !this.seen.has(id));

      if (fresh.length === 0) {
        return;
      }
      fresh.forEach((id) => this.seen.add(id));
      if (!this.animate()) {
        return;
      }

      this.entering.update((current) => {
        const next = new Set(current);
        fresh.forEach((id) => next.add(id));
        return next;
      });
      this.schedule(() => {
        this.entering.update((current) => {
          const next = new Set(current);
          fresh.forEach((id) => next.delete(id));
          return next;
        });
      }, ENTER_MS);
    });

    this.destroyRef.onDestroy(() => {
      this.timers.forEach((timer) => globalThis.clearTimeout(timer));
      this.timers.clear();
    });
  }

  public isEntering(id: string): boolean {
    return this.entering().has(id);
  }

  public isLeaving(id: string): boolean {
    return this.leaving().has(id);
  }

  public toastDataCy(id: string): string {
    return `notification-toast-${id}`;
  }

  /** Fechar remove o aviso, mas NÃO marca como lida: fechar não é ler. */
  public onCloseToast(id: string): void {
    this.beginLeave(id);
  }

  /** Expirar também só remove. */
  public onExpired(id: string): void {
    this.beginLeave(id);
  }

  /**
   * A ação contextual marca como lida, abre o destino e fecha (CA 04 da DEV-872; CA 01 e CA 02 da
   * DEV-895).
   *
   * O aviso é lido da fila ANTES do `beginLeave()`: a saída animada só remove da fila depois do
   * timer, mas depender dessa ordem para achar o payload deixaria a navegação à mercê da duração da
   * animação. Sem destino, o comportamento antigo permanece — marca como lida e fecha.
   */
  public onCtaSelect(id: string): void {
    const notification = this.state.toasts().find((toast) => toast.id === id);

    this.state.markRead(id);

    if (notification) {
      this.navigation.open(notification);
    }

    this.beginLeave(id);
  }

  private beginLeave(id: string): void {
    if (this.leaving().has(id)) {
      return;
    }
    if (!this.animate()) {
      // Sem animação não há o que esperar.
      this.state.dismissToast(id);
      return;
    }

    this.leaving.update((current) => new Set(current).add(id));
    this.schedule(() => {
      this.leaving.update((current) => {
        const next = new Set(current);
        next.delete(id);
        return next;
      });
      this.state.dismissToast(id);
    }, LEAVE_MS);
  }

  /**
   * Único dono da limpeza do `seen`. O `beginLeave()` não apaga o id: três caminhos tiram o aviso
   * da fila sem passar por ele — o teto de três avisos e o descarte da fila ao abrir o painel
   * (`notifications-state.service.ts`), e a saída sem animação, que chama `dismissToast()` direto.
   * Derivar da fila viva cobre os três de uma vez, em vez de um `delete` por caminho.
   */
  private pruneSeen(liveIds: readonly string[]): void {
    if (this.seen.size === 0) {
      return;
    }
    const live = new Set(liveIds);
    this.seen.forEach((id) => {
      if (!live.has(id)) {
        this.seen.delete(id);
      }
    });
  }

  private schedule(action: () => void, delayMs: number): void {
    const timer = globalThis.setTimeout(() => {
      this.timers.delete(timer);
      action();
    }, delayMs);
    this.timers.add(timer);
  }

  /**
   * A preferência é consultada na exibição e reagimos à mudança com a aplicação já aberta (CA 08).
   * Guarda de ambiente no mesmo padrão do IntersectionObserver na infinite-scroll.directive.
   */
  private watchReducedMotion(): void {
    if (typeof globalThis.matchMedia === "undefined") {
      return;
    }
    const query = globalThis.matchMedia(REDUCED_MOTION_QUERY);
    this.reducedMotion.set(query.matches);

    const onChange = (event: MediaQueryListEvent): void => this.reducedMotion.set(event.matches);
    query.addEventListener("change", onChange);
    this.destroyRef.onDestroy(() => query.removeEventListener("change", onChange));
  }
}
